import type { UseQueryResult } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";

import type { QueryParams } from "../../types";

import { apiHooks } from "./config";
import type {
    SearchBrandedSplitDataSchema,
    SearchKeywordsDataSchema,
    SearchRankDistributionDataSchema,
    SearchTrafficDataSchema,
} from "./types";
import { fetchData } from "./utils";

interface ApiConfig<T> {
    queryKey: string;
    endpoint: string;
}

const createApiHook =
    <T>(config: ApiConfig<T>) =>
    (params: QueryParams): UseQueryResult<T> => {
        const { queryKey, endpoint } = config;
        return useQuery<T>({
            queryKey: [queryKey, params],
            queryFn: () => fetchData<T>(endpoint, params),
            enabled: true,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
        });
    };

type ApiHookConfigMap = {
    useTrafficData: SearchTrafficDataSchema;
    useOrganicKeywordsData: SearchKeywordsDataSchema;
    usePaidKeywordsData: SearchKeywordsDataSchema;
    useRankDistributionData: SearchRankDistributionDataSchema;
    useBrandedSplitData: SearchBrandedSplitDataSchema;
};

const createApiHooks = () =>
    Object.fromEntries(
        Object.entries(apiHooks).map(([hookName, config]) => [hookName, createApiHook<any>(config)]),
    ) as {
        [K in keyof ApiHookConfigMap]: (params: QueryParams) => UseQueryResult<ApiHookConfigMap[K]>;
    };

export const {
    useTrafficData,
    useOrganicKeywordsData,
    usePaidKeywordsData,
    useRankDistributionData,
    useBrandedSplitData,
} = createApiHooks();
